import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { products } from '../data/mockData';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShoppingCart, Eye, Home, ChevronRight, ShoppingBag } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const product = products.find(p => p.id === parseInt(id));
  const [selectedVariant, setSelectedVariant] = useState(product?.variants?.[0] || null);

  if (!product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white text-xl">Product not found</p>
      </div>
    );
  }

  const handleAddToCart = () => {
    if (selectedVariant) {
      addToCart({
        ...product,
        price: selectedVariant.price,
        duration: selectedVariant.duration,
        variantId: `${product.id}-${selectedVariant.duration}`
      });
      toast.success('Added to cart!', {
        description: `${product.name} - ${selectedVariant.duration}`,
      });
    }
  };

  const handleBuyNow = () => {
    if (selectedVariant) {
      addToCart({
        ...product,
        price: selectedVariant.price,
        duration: selectedVariant.duration,
        variantId: `${product.id}-${selectedVariant.duration}`
      });
      navigate('/checkout');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <Toaster />
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center text-gray-400 text-sm mb-8">
            <Eye size={16} className="mr-2" />
            <span>/</span>
            <Link to="/products" className="ml-2 hover:text-white transition-colors">
              Product
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white">{product.name}</span>
          </div>

          {/* Product Title */}
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-12">{product.name}</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Show Showcase Button */}
              <button className="w-full mt-6 bg-gray-800 hover:bg-gray-700 text-white py-4 rounded-lg flex items-center justify-center transition-colors">
                <Eye size={20} className="mr-2" />
                Show Showcase
              </button>

              {/* Features List */}
              <Card className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10 mt-6">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">Features</h3>
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-300">
                        <span className="text-purple-400 mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Description */}
              <Card className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10 mt-6">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">Description</h3>
                  <p className="text-gray-300">{product.description}</p>
                </CardContent>
              </Card>
            </div>

            {/* Pricing Variants */}
            <div>
              <Card className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-white/10">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-400 mb-4">Variants:</h3>
                  <div className="space-y-3 mb-6">
                    {product.variants.map((variant, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedVariant(variant)}
                        className={`w-full p-4 rounded-lg border-2 transition-all flex items-center justify-between ${
                          selectedVariant?.duration === variant.duration
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                      >
                        <span className="text-white font-medium text-lg">{variant.duration}</span>
                        <span className="bg-gray-800 text-white px-4 py-2 rounded-md font-semibold">
                          ${variant.price.toFixed(2)}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      onClick={handleBuyNow}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg flex items-center justify-center"
                    >
                      <ShoppingBag className="mr-2" size={20} />
                      Buy Now
                    </Button>
                    <Button
                      onClick={handleAddToCart}
                      variant="outline"
                      className="w-full border-white/20 text-white hover:bg-white/10 py-6 text-lg flex items-center justify-center"
                    >
                      <ShoppingCart className="mr-2" size={20} />
                      Add To Cart
                    </Button>
                  </div>

                  {/* Security Info */}
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="text-sm text-gray-400 space-y-2">
                      <p>✓ Instant delivery after purchase</p>
                      <p>✓ 24/7 customer support</p>
                      <p>✓ Secure payment processing</p>
                      <p>✓ Regular updates included</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetail;